import React, {
    useContext,
    useCallback,
    useState,
    useEffect
} from 'react';
import { useTranslator } from '@jutro/locale';
import _ from 'lodash';
import moment from 'moment';
import appConfig from 'app-config';
import PropTypes from 'prop-types';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { MLAgentPolicySummaryHeaderComponent } from 'gw-capability-quoteandbind-common-react';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import { MLPAUMBCoveragesComponent, MLReadOnlyCoverageComponent } from 'ml-components-common-react';
import PolicySummaryExclusionsComponent from '../PolicysSummaryExclusionsComponent/PolicySummaryExclusionsComponent';
import messages from './PUPPolicySummaryComponent.messages';
import metadata from './PUPPolicySummaryComponent.metadata.json5';
import styles from './PUPPolicySummaryComponent.module.scss';

function PUPPolicySummaryComponent(props) {
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const {
        value: policyDetails,
        startPrint,
        updateStartPrint,
        editCoverages,
        editDrivers,
        editPayment,
        editEffectiveDate,
        cancelChanges,
        makeMoreChanges,
        submitChanges,
        currentSteps,
        currentStepIndex,
        goToStep,
        selectablePath,
        detailsLoaded,
        isPolicyChange,
        gpaMode,
        isModernProduct
    } = props;
    const policyAdditionalInterests = _.get(policyDetails, 'additionalInterests.value', []);
    const quoteId = _.get(policyDetails, 'quoteId.value', '');
    const ampMode = appConfig.portalCode === 'AMP';
    const qbMode = appConfig.portalCode === 'QB';
    const visitedSteps = currentSteps ? currentSteps.slice(0, currentStepIndex) : undefined;
    const [policyChangeHistories, setPolicyChangeHistories] = useState([]);

    const setPolicyFromPolicyDetails = useCallback(() => {
        const lobs = _.get(policyDetails, 'lobs.value', undefined);
        const lobsPolChange = _.get(policyDetails, 'lobsPolChange.value', undefined);
        if (lobs && lobs.personalUmbrella) {
            return policyDetails.lobs.personalUmbrella;
        }
        if (lobsPolChange && lobsPolChange.personalUmbrella) {
            return policyDetails.lobsPolChange.personalUmbrella;
        }
    }, [policyDetails]);

    const policy = setPolicyFromPolicyDetails();
    const policyCoverables = _.get(policy, 'coverables', undefined);
    const coverages = _.get(policyDetails, 'coverageLobs.personalUmbrella', undefined);

    const getSelectedCoverages = useCallback(() => {
        if (_.isUndefined(coverages.value)) {
            return [];
        }

        const displayableCoverages = [
            ...(coverages.primaryCoverages.value || []),
            ...(coverages.prsnlAutoExcessCoverages.value || []),
            ...(coverages.prsnlWatercraftExcessCoverages.value || [])
        ].filter((cov) => cov.selected);

        return displayableCoverages;
    }, [coverages]);

    const displayableCoverages = getSelectedCoverages();

    const additionalCoverages = _.get(coverages, 'additionalCoverages.value', []).filter((cov) => cov.selected);

    const getTermValueFromAvailableCoverages = useCallback((covName) => {
        if (coverages.value && coverages.value.primaryCoverages) {
            const foundCov = coverages.value.primaryCoverages.find((cov) => cov.name === covName);
            if (foundCov) {
                return foundCov.terms[0].chosenTermValue;
            }
        }
        console.error(new Error(`Could not retrieve term value since there is no '${covName}' coverage`));
        return null;
    }, [coverages]);

    const policyLimitAmount = getTermValueFromAvailableCoverages('Liability');
    const selfInsuredRetentionAmount = getTermValueFromAvailableCoverages('Self Insured Retention');

    const getUnderlyingPolicies = () => {
        const limits = '$250,000/$500,000 or $500,000';
        const smallLimits = '$250,000/$500,000 or $300,000';
        const smallLimit = '$300,000';
        return [
            { type: 'Automobile Liability', value: limits },
            { type: 'Automobile Uninsured-Underinsured Motorist Bodily Injury ', value: limits },
            { type: 'Homeowners Liability', value: smallLimit },
            { type: 'Dwelling Fire or Landlord Liability', value: smallLimit },
            { type: 'Watercraft Liability', value: smallLimits },
            { type: 'Recreational Vehicle Liability', value: limits },
            { type: 'Motorcycle Liability', value: limits },
            { type: 'Motorcycle Uninsured-Underinsured Motorists Bodily Injury', value: limits },
        ];
    };

    const renderPolicyHeader = useCallback(() => {
        let headerMessage;
        if(ampMode && isPolicyChange) {
            headerMessage = translator(messages.policyX, { policyId: (_.get(policyDetails, 'policyId.value', ''))})
        } 
        if(qbMode) {
            headerMessage = translator(messages.quoteX, { quoteId: (quoteId) })
        }
        return headerMessage;
    },[ampMode, isPolicyChange, policyDetails, qbMode, quoteId, translator])
    
    const convertLocalDateToDate = useCallback((localDate) => {
        const parsedDate = new Date(localDate.year, localDate.month, localDate.day);
        return moment(parsedDate).format('MM/DD/YYYY');
    },[]);

    const underlyingPolicies = getUnderlyingPolicies();

    const getNameCell = (item) => {
        return _.capitalize(item.contact.displayName.value);
    };

    const getGenderCell = (item) => {
        const genderTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('GenderType');
        const gender = genderTypeList.getCode(item.contact.gender.value.code).name;
        return translator({
            id: gender,
            defaultMessage: gender
        });
    };

    const getDOBCell = (item) => {
        const dateOfBirth = MLLocalDateUtil.localDate2Date(item.contact.dateOfBirth.value, true);
        return dateOfBirth;
    };

    const getMaritalStatusCell = (item) => {
        const maritalStatusCode = _.get(item, 'contact.maritalStatus.value.code', undefined);
        if (maritalStatusCode) {
            const maritalStatusTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('MaritalStatus');
            const maritalStatus = maritalStatusTypelist.getCode(maritalStatusCode).name;
            return translator({
                id: maritalStatus,
                defaultMessage: maritalStatus
            });
        }
        return '';
    };

    const getUnderlyingPolicyTypeCell = (item) => {
        return item.type;
    };

    const getUndPolLimitCell = (item) => {
        return gpaMode ? `${item.value} ${translator(messages.csl)}` : item.value;
    };

    const renderWatercraftOperatorsTitle = () => {
        return (
            <h3 className="subsection-header">
                {translator(messages.watercraftOperators)}
            </h3>
        );
    };

    const showWatercraftOperators = (operators) => {
        return operators.length > 0 && (!ampMode || isPolicyChange);
    };

    const generateVehiclesOverrides = useCallback(() => {
        const vehicles = _.get(policyCoverables, 'vehicles.children', []);
        const watercrafts = _.get(policyCoverables, 'watercrafts.children', []);

        const getVehicleDetails = (vehicle, isWatercraft = false) => {
            return (
                <>
                    <span className="font-weight-bold">
                        {vehicle.year.value}
                        &nbsp;
                        {vehicle.make.value}
                    </span>
                    &nbsp;
                    {vehicle.model.value}
                    {!isWatercraft && (
                        <>
                            &nbsp;
                            {vehicle.trim_ML.value}
                        </>
                    )}
                </>
            );
        };

        const vehicleOverrides = vehicles.map((vehicle, index) => {
            return {
                [`vehicleDetails${index}`]: {
                    content: getVehicleDetails(vehicle)
                }
            };
        });

        const watercraftOverrides = watercrafts.map((watercraft, index) => {
            return {
                [`watercraftsDetails${index}`]: {
                    content: getVehicleDetails(watercraft, true)
                }
            };
        });

        return Object.assign({}, ...vehicleOverrides, ...watercraftOverrides);
    }, [policyCoverables]);

    useEffect(() => {
        // get all steps, and then filter by the current steps
        if(ampMode && isPolicyChange && visitedSteps){
            const selectableSteps = visitedSteps.filter((policyChangeHistory) => {
                if(policyChangeHistory.path !== '/additionalchanges'){
                    return selectablePath[policyChangeHistory.path];
                }
                return null
            })
            setPolicyChangeHistories(selectableSteps)
        }
    },[])

    const generatePolicyChangeHistoriesOverrides = () => {
        const policyChangeHistoriesOverrides = policyChangeHistories.map((policyChangeHistory, index) => {
            return {
                [`policyChangeHistoryDiv${index}`]: {
                    className: (policyChangeHistories.length - 1) === index ? 'vertical-spacing-40' : 'vertical-spacing-20'
                },
                [`endorsementChangeName${index}`]: {
                    content: translator(selectablePath[policyChangeHistory.path])
                },
                [`endorsementTextLink${index}`]: {
                    onClick: () => goToStep(policyChangeHistory.path)
                }

            }
        })

        return Object.assign({}, ...policyChangeHistoriesOverrides);
    }

    const generatePropertiesOverrides = useCallback(() => {
        const locations = _.get(policyCoverables, 'locations.children', []);

        const locationsOverrides = locations.map((location, index) => {
            return {
                [`propertiesAddressLine1${index}`]: {
                    visible: !!_.get(location, 'address.addressLine1.value', undefined),
                    content: _.get(location, 'address.addressLine1.value', '')
                },
                [`propertiesAddressLine2${index}`]: {
                    visible: !!_.get(location, 'address.addressLine2.value', undefined),
                    content: _.get(location, 'address.addressLine2.value', '')
                },
                [`propertiesAddressLine3${index}`]: {
                    visible: !!_.get(location, 'address.addressLine3.value', undefined),
                    content: _.get(location, 'address.addressLine3.value', '')
                },
                [`propertiesCityStateCode${index}`]: {
                    content: `${location.address.city.value}, ${location.address.state.value.code} ${location.address.postalCode.value}`
                }
            };
        });

        return Object.assign({}, ...locationsOverrides);
    }, [policyCoverables]);

    const generateWatercraftOperatorsOverrides = useCallback(() => {
        const operatorsOverrides = policyDetails.watercraftOperators_ML.value.map((op, index) => {
            return {
                [`watercraftOperator${index}`]: {
                    content: _.get(op, 'contact.displayFullName_Ext')
                }
            }
        });

        return Object.assign({}, ...operatorsOverrides);
    }, [policyDetails]);

    const resolvers = {
        resolveComponentMap: {
            mlPAUMBCoveragesComponent: MLPAUMBCoveragesComponent,
            mlReadOnlyCoverageComponent: MLReadOnlyCoverageComponent,
            policySummaryExclusionsComponent: PolicySummaryExclusionsComponent,
            MLAgentPolicySummaryHeaderComponent:MLAgentPolicySummaryHeaderComponent
        },
        resolveCallbackMap: {
            getNameCell,
            getGenderCell,
            getDOBCell,
            getMaritalStatusCell,
            getUnderlyingPolicyTypeCell,
            getUndPolLimitCell,
            editCoverages
        }
    };

    const watercraftOperators = _.get(policyDetails, 'watercraftOperators_ML.children', []);
    const overrideProps = {
        policySummaryInsuredInfoComponent: {
            model: policyDetails,
            isPUP: true,
            gpaMode : gpaMode
            // visible : !gpaMode
        },
        agentPolicySummaryComponent: {
            visible: gpaMode,
            model: policyDetails,
        },
        policyHeader: {
            content: renderPolicyHeader(),
            visible : !gpaMode && ((isPolicyChange && ampMode) || qbMode),
        },
        limitRetentionContainer: {
            visible: gpaMode
        },
        policyLimit: {
            content: policyLimitAmount
        },
        retention: {
            content: selfInsuredRetentionAmount
        },
        summaryAdditionalInterestsContainer: {
            visible: policyAdditionalInterests.length > 0 && !gpaMode          
        },
        policySummaryAdditionalInterestComponent: {
            model: policyDetails,
            isPUP: true
        },
        hrLine1: {
            visible: policyAdditionalInterests.length > 0
        },
        ampChangeEffectiveDateContainer: {
            visible: ampMode && isPolicyChange
        },
        changeEffectiveDateSectionHeader: {
            content: translator(messages.changeEffectiveDate, {date: convertLocalDateToDate(_.get(policyDetails, 'effective_ML.value', ''))})
        },
        changeConfirmationText: {
            content: translator(messages.changeConfirmationText)
        },
        policyChangeHistoryIterable: {
            data: policyChangeHistories
        },
        policySummaryConfirmationBoxComponent: {
            visible: qbMode || (isPolicyChange && ampMode), 
            ampMode,
            qbMode,
            model: policyDetails,
            startPrint,
            updateStartPrint,
            editPayment,
            editEffectiveDate,
            cancelChanges,
            makeMoreChanges,
            submitChanges,
            detailsLoaded,
        },
        policySummaryDriversComponent: {
            model: policyCoverables,
            editDrivers,
            gpaMode,
            isPolicyChange,
            isPUP: true,
            isModernProduct
        },
        watercraftOperatorsIterable: {
            visible: isPolicyChange,
            data: policyDetails.watercraftOperators_ML.value
        },
        watercraftOperatorTable: {
            visible: !isPolicyChange,
            data: watercraftOperators,
            renderTitle: renderWatercraftOperatorsTitle,
            title: translator(messages.watercraftOperators)
        },
        watercraftOperatorsContainer: {
            visible: showWatercraftOperators(watercraftOperators)
        },
        hrLine4: {
            visible: showWatercraftOperators(watercraftOperators)
        },
        exposuresRowContainer: {
            visible: policyCoverables.vehicles.length || policyCoverables.watercrafts.length || policyCoverables.locations.length
        },
        exposurVehiclesContainer: {
            visible: policyCoverables.vehicles.length || policyCoverables.watercrafts.length
        },
        vehicleIterable: {
            data: policyCoverables.vehicles.children
        },
        watercraftsIterable: {
            data: policyCoverables.watercrafts.children
        },
        propertiesIterable: {
            data: policyCoverables.locations.children
        },
        coveragesRowContainer: {
            visible: displayableCoverages.length > 0
        },
        editCoverageLink: {
            visible: (isPolicyChange && ampMode) || qbMode,
            content: translator(messages.editCoverages),
            onClick: editCoverages
        },
        mlPAUMBCoveragesComponent: {
            isUmb: true,
            coverages: displayableCoverages,
            displayHelpMessageLimit: false
        },
        addtlCovRowContainer: {
            visible: additionalCoverages.length > 0
        },
        editCovBtn2: {
            visible: !ampMode
        },
        mlReadOnlyCoverageComponent: {
            coverages: additionalCoverages,
            displayHelpMessage: !ampMode,
            showEditCoverage: isPolicyChange && ampMode,
            editCoverage: editCoverages,
            isModernProduct: isModernProduct
        },
        underlyingInsuranceHeader: {
            content: gpaMode ? translator(messages.gpaUnderlyingInsuranceHeader) : translator(messages.underlyingInsuranceHeader)
        },
        underlyingInsuranceTable: {
            data: underlyingPolicies
        },
        policySummaryExclusionsComponent: {
            policy: policyCoverables,
            coverages: coverages
        },
        locationsContainer: {
            visible: policyCoverables.locations.children.length > 0
        },
        exposuresContainer: {
            visible: policyCoverables.vehicles.children.length > 0 || policyCoverables.watercrafts.children.length > 0 || policyCoverables.locations.children.length > 0
        },
        ampInsuranceCoverageText: {
            visible: isPolicyChange && ampMode
        },
        ampPolicySummaryConfirmationBoxComponent: {
            visible: isPolicyChange && ampMode,
            ampMode: ampMode,
            model: policyDetails,
            startPrint,
            editPayment,
            editEffectiveDate,
            cancelChanges,
            makeMoreChanges,
            submitChanges,
            detailsLoaded,
            updateStartPrint
        },
        billingSummary: {
            visible: gpaMode,
            content: (
                <div className='vertical-spacing-40'>
                    <h2 className='section-header vertical-spacing-20'>
                        Billing
                    </h2>                   
                    <p><strong>{policyDetails.paymentDetails.paymentMethod.value}</strong></p>
                   
                </div>
            )

        },
        discountsContainer :{
            visible : gpaMode && policyDetails.discounts.value && policyDetails.discounts.length  > 0
        },
        discountIterable : {
            data : policyDetails.discounts.value
        },
        coveragesContainer: {
            className: qbMode ? 'align-items-baseline vertical-spacing-20' : 'align-items-baseline vertical-spacing-20 flex-space-between flex-wrapper'
        },
        ...generateVehiclesOverrides(),
        ...generatePropertiesOverrides(),
        ...generateWatercraftOperatorsOverrides(),
        ...generatePolicyChangeHistoriesOverrides()
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            componentMap={resolvers.resolveComponentMap}
            model={policyDetails}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={styles}
        />
    );
}

PUPPolicySummaryComponent.propTypes = {
    value: PropTypes.shape({}).isRequired,
    startPrint: PropTypes.bool,
    isPolicyChange: PropTypes.bool,
    updateStartPrint: PropTypes.func,
    editCoverages: PropTypes.func.isRequired,
    editPayment: PropTypes.func.isRequired,
    editDrivers: PropTypes.func.isRequired,
    gpaMode:PropTypes.bool
};

PUPPolicySummaryComponent.defaultProps = {
    startPrint: false,
    updateStartPrint: _.noop,
    isPolicyChange: false,
    gpaMode : false

};

export default PUPPolicySummaryComponent;
